<template>
  <CFlex justify="end" class="u-mb-2">
    <CSelect :options="[true, false]" v-model:value="includeVat">
      <template #option="{ option }">
        {{
          option
            ? $t("approvalModal.showPricesWithVat")
            : $t("approvalModal.showPricesWithoutVat")
        }}
      </template>
    </CSelect>
  </CFlex>

  <CTable class="c-job-approval-table">
    <thead>
      <tr>
        <th>{{ $t("approvalModal.tableDescriptionHeading") }}</th>
        <th style="width: 6rem">{{ $t("approvalModal.tableOrderHeading") }}</th>
        <th style="width: 10rem">{{ $t("approvalModal.tableDmsPriceHeading") }}</th>
        <th style="width: 10rem">{{ $t("approvalModal.tablePriceHeading") }}</th>
        <th style="width: 6rem">{{ $t("approvalModal.tableWarrantyHeading") }}</th>
      </tr>
    </thead>

    <tbody>
      <tr v-for="job in jobs">
        <td class="u-title">
          <CEllipsis>{{ job.getTitle() }}</CEllipsis>
        </td>

        <td>
          <CJobApprovalOrderStatusButton :context :job @click="updateOrderStatus(job)" />
        </td>

        <td>
          <CButton
            v-if="job.canCopyDmsPrice()"
            theme="normal-primary"
            @click="job.copyDmsPrice()"
            v-tooltip="$t('approvalModal.copyDmsPrice')"
          >
            <CFlex justify="between">
              {{
                formatAmount(
                  includeVat ? job.price.dmsAmountWithVat : job.price.dmsAmountWithoutVat
                )
              }}
              <CIcon name="copy" />
            </CFlex>
          </CButton>
          <CCard v-else class="u-bold dms-price-cell">
            {{
              formatAmount(
                includeVat ? job.price.dmsAmountWithVat : job.price.dmsAmountWithoutVat
              ) || $t("approvalModal.emptyPrice")
            }}
          </CCard>
        </td>

        <td>
          <CButton
            @click="updatePrice(job)"
            :theme="getPriceApprovalStatusButtonTheme(job.priceApproval.status)"
            v-tooltip="getPriceApprovalStatusLabel(job.priceApproval.status)"
          >
            {{
              formatAmount(
                includeVat ? job.price.amountWithVat : job.price.amountWithoutVat
              ) || $t("approvalModal.emptyPrice")
            }}
            <CPriceApprovalStatusIcon
              :status="job.priceApproval.status"
              :hasSignature="job.order.hasSignature"
            />
          </CButton>
        </td>

        <td>
          <CDropdown class="c-job-approval-table-warranty">
            <template #icon>
              <CWarrantyIcon :status="job.warrantyStatus" />
            </template>

            <CButton
              v-for="status in enumValues(WarrantyStatus)"
              @click="updateWarranty(job, status)"
            >
              <CWarranty :status />
            </CButton>
          </CDropdown>
        </td>
      </tr>
    </tbody>
    <tfoot>
      <tr>
        <td>{{ $t("approvalModal.total") }}</td>
        <td />
        <td class="dms-price-total-cell">{{ dmsPriceTotal }}</td>
        <td class="dms-price-approval-cell">{{ priceTotal }}</td>
        <td />
      </tr>
    </tfoot>
  </CTable>

  <CFlex class="u-mt-2">
    <CButton
      v-if="jobs.some((j) => j.canCopyDmsPrice())"
      theme="normal-primary"
      @click="() => jobs.forEach((j) => j.copyDmsPrice())"
    >
      <CIcon name="copy" />
      {{ $t("approvalModal.copyAllDmsPrices") }}
    </CButton>

    <CFill />

    <CButton
      v-if="orderApprovableJobs.length > 0 || priceApprovableJobs.length > 0"
      theme="main-primary"
      @click="approveAll"
    >
      <CIcon name="thumbs-up" />
      {{ $t("approvalModal.approveAll") }}
    </CButton>
  </CFlex>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import Modal from '@/general/utils/Modal';
  import CCard from '@/shared/components/CCard.vue';
  import CEllipsis from '@/shared/components/CEllipsis.vue';
  import CFill from '@/shared/components/CFill.vue';
  import CFlex from '@/shared/components/CFlex.vue';
  import CIcon from '@/shared/components/CIcon.vue';
  import CTable from '@/shared/components/CTable.vue';
  import CButton from '@/shared/components/buttons/CButton.vue';
  import CDropdown from '@/shared/components/dropdown/CDropdown.vue';
  import CSelect from '@/shared/components/form/CSelect.vue';
  import { formatAmount } from '@/shared/utils/format';
  import { enumValues, isAnyOf, isPresent } from '@/shared/utils/typescript';
  import OrderStatus from '../constants/OrderStatus';
  import PriceApprovalStatus from '../constants/PriceApprovalStatus';
  import WarrantyStatus from '../constants/WarrantyStatus';
  import Job, { JobApprovalData } from '../models/Job';
  import JobContext from '../models/JobContext';
  import { getPriceApprovalStatusLabel } from '../utils/labels';
  import { getPriceApprovalStatusButtonTheme } from '../utils/themes';
  import CWarranty from './CWarranty.vue';
  import CWarrantyIcon from './CWarrantyIcon.vue';
  import CJobApprovalOrderStatusButton from './approval/CJobApprovalOrderStatusButton.vue';
  import CPriceApprovalStatusIcon from './approval/CPriceApprovalStatusIcon.vue';

  const PriceModal = () => import('../modals/PriceModal.vue');
  const JobOrderApprovalModal = () =>
    import('../modals/approval/JobOrderApprovalModal.vue');

  const props = defineProps<{
    jobs: Array<Job>;
    context: JobContext;
    formData: JobApprovalData;
  }>();

  const i18n = useI18n();

  const includeVat = ref(true);

  const orderApprovableJobs = computed(() =>
    props.jobs.filter(
      (job) => !job.order.hasSignature && job.order.status !== OrderStatus.APPROVED,
    ),
  );

  const priceApprovableJobs = computed(() => {
    if (!props.formData.hasPriceApprovalPermission) return [];
    return props.jobs.filter(
      (job) =>
        !job.order.hasSignature
        && isPresent(job.price.amountWithoutVat)
        && job.priceApproval.status !== PriceApprovalStatus.APPROVED,
    );
  });

  function updateWarranty(job: Job, status: WarrantyStatus): void {
    job.warrantyStatus = status;

    if (job.warrantyStatus === WarrantyStatus.WARRANTY) {
      job.price.amountWithoutVat = 0;
      job.price.amountWithVat = 0;
    } else if (
      isAnyOf(job.warrantyStatus, WarrantyStatus.PARTIAL, WarrantyStatus.NO_WARRANTY)
    ) {
      job.price.amountWithoutVat = job.original?.price.amountWithoutVat ?? null;
      job.price.amountWithVat = job.original?.price.amountWithVat ?? null;
    } else {
      job.price.amountWithoutVat = null;
      job.price.amountWithVat = null;
    }
  }

  const dmsPriceTotal = computed(() => {
    const amount = props.jobs.reduce(
      (total, job) =>
        total
        + (includeVat.value ? job.price.dmsAmountWithVat : job.price.dmsAmountWithoutVat),
      0,
    );
    return amount > 0 ? formatAmount(amount) : i18n.t('approvalModal.emptyPrice');
  });

  const priceTotal = computed(() => {
    const approvedJobs = props.jobs.filter(
      (j) => j.priceApproval.status === PriceApprovalStatus.APPROVED,
    );
    const amount = approvedJobs.reduce(
      (total, job) =>
        total + (includeVat.value ? job.price.amountWithVat : job.price.amountWithoutVat),
      0,
    );
    return amount > 0 ? formatAmount(amount) : i18n.t('approvalModal.emptyPrice');
  });

  function approveAll() {
    orderApprovableJobs.value.forEach((job) => (job.order.status = OrderStatus.APPROVED));
    priceApprovableJobs.value.forEach(
      (job) => (job.priceApproval.status = PriceApprovalStatus.APPROVED),
    );
  }

  async function updatePrice(job: Job) {
    const result = await Modal.openAndWait(PriceModal, { job, context: props.context });
    job.updatePrice(result);
  }

  async function updateOrderStatus(job: Job) {
    const data = await Modal.openAndWait(JobOrderApprovalModal, {
      job,
      context: props.context,
      skipAuth: true,
    });
    job.order.status = data.approvalStatus;
  }
</script>

<style lang="scss">
.c-job-approval-table {
  table {
    // fixed layout is needed to make the first column reliably fill remaining space in conjunction with ellipsis
    // unfortunately we need to specify explicit widths on all other columns to make this work
    table-layout: fixed;
  }

  th {
    color: $neutral-500;
  }

  td {
    white-space: nowrap;
  }

  td > * {
    width: 100%;
    height: 100%;
  }

  &-warranty {
    .c-direction-icon:not(:first-child) {
      margin: 0;
    }
  }

  .dms-price-cell {
    line-height: 1.125;
    padding: 0.5rem 0.75rem;
  }

  .dms-price-total-cell {
    padding-left: 1.25rem;
    padding-right: 1.25rem;
  }

  .dms-price-approval-cell {
    width: 100%;
    display: inline-flex;
    justify-content: center;
    padding-right: 1.625rem;
  }
}
</style>
